import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";

export const Eyebrow = ({ children, className = "", dark = false }) => (
  <p className={`eyebrow ${dark ? "text-brand-green" : "text-brand"} ${className}`}>{children}</p>
);

export const SectionHeading = ({ eyebrow, title, intro, action, id }) => (
  <div className="mb-10 flex flex-col gap-6 border-t border-ink pt-6 md:flex-row md:items-end md:justify-between">
    <div className="max-w-2xl">
      {eyebrow && <Eyebrow className="mb-3">{eyebrow}</Eyebrow>}
      <h2 id={id} className="text-2xl font-extrabold tracking-tight text-brand sm:text-3xl lg:text-4xl">{title}</h2>
      {intro && <p className="mt-4 text-base leading-relaxed text-ink-muted">{intro}</p>}
    </div>
    {action}
  </div>
);

export const ArrowLink = ({ to, children, testId, dark = false }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group inline-flex items-center gap-2 text-sm font-bold underline-offset-4 hover:underline ${dark ? "text-white" : "text-brand"}`}
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
  </Link>
);

export const ButtonLink = ({ to, children, testId, variant = "primary" }) => {
  const styles = {
    primary: "bg-brand text-white hover:bg-[#0B3D8A]",
    outline: "border border-brand text-brand hover:bg-brand hover:text-white",
    green: "bg-brand-green text-brand-black hover:bg-[#B8EC6B]",
    light: "border border-white/50 text-white hover:bg-white hover:text-brand",
  };
  return (
    <Link to={to} data-testid={testId} className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors duration-200 ${styles[variant]}`}>
      {children}
    </Link>
  );
};

export const PageHeader = ({ eyebrow, title, intro, children, aside }) => (
  <section className="border-b border-line bg-white">
    <div className={`container-x py-14 md:py-20 ${aside ? "grid items-center gap-10 lg:grid-cols-12 lg:gap-12" : ""}`}>
      <div className={aside ? "lg:col-span-7" : ""}>
      {eyebrow && <Eyebrow className="mb-4 fade-up">{eyebrow}</Eyebrow>}
      <h1 className="max-w-4xl text-4xl font-extrabold tracking-tight text-brand sm:text-5xl lg:text-6xl fade-up" style={{ animationDelay: "60ms" }}>
        {title}
      </h1>
      {intro && (
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg fade-up" style={{ animationDelay: "120ms" }}>
          {intro}
        </p>
      )}
      {children}
      </div>
      {aside && <div className="fade-up lg:col-span-5" style={{ animationDelay: "180ms" }}>{aside}</div>}
    </div>
  </section>
);

export const CategoryTag = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-[0.14em] text-brand">{children}</span>
);

export const MetaRow = ({ items, className = "" }) => (
  <ul className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-muted ${className}`}>
    {items.filter(Boolean).map((it, i) => (
      <li key={i} className="flex items-center gap-1.5">
        {it.icon && <it.icon className="h-4 w-4 text-brand" aria-hidden="true" />}
        <span>{it.label}</span>
      </li>
    ))}
  </ul>
);

export const PendingAction = ({ label, note, testId }) => (
  <div className="rounded-sm border border-dashed border-line bg-mist p-4" data-testid={testId}>
    <span aria-disabled="true" className="inline-flex cursor-not-allowed items-center rounded-full bg-line px-5 py-2.5 text-sm font-bold text-ink-muted">
      {label}
    </span>
    <p className="mt-3 flex items-start gap-2 text-sm text-ink-muted">
      <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      {note}
    </p>
  </div>
);

export const EmptyState = ({ title, message, action, testId }) => (
  <div className="flex flex-col items-start gap-4 border border-line bg-mist p-8 md:p-12" data-testid={testId} role="status">
    <h3 className="text-xl font-bold text-brand">{title}</h3>
    <p className="max-w-lg text-ink-muted">{message}</p>
    {action}
  </div>
);

export const LoadingState = ({ label = "Loading content", className = "", testId = "loading-state" }) => (
  <div role="status" aria-live="polite" className={`animate-pulse bg-mist ${className}`} data-testid={testId}>
    <span className="sr-only">{label}…</span>
  </div>
);

export const ErrorState = ({ testId = "error-state" }) => (
  <EmptyState title="Content could not be loaded" message="Please check your connection and try again in a moment." testId={testId} />
);
